import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { fmt } from "../lib/api";

const TIER = {
  1: { label: "T1 RAW", cls: "bg-[#1E293B] text-[#38BDF8] border-[#0284C7]" },
  2: { label: "T2 PREPROD", cls: "bg-[#312E81]/60 text-[#A5B4FC] border-[#6366F1]" },
  3: { label: "T3 FINISHED", cls: "bg-[#064E3B]/70 text-[#6EE7B7] border-[#10B981]" },
};
export const TIER_NAMES = { 1: "Raw Materials", 2: "Preproduction", 3: "Finished Goods" };

export const TierBadge = ({ tier, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] font-bold tracking-widest rounded-sm ${TIER[tier].cls}`}>
    {TIER[tier].label}
  </span>
);

export const isLow = (it) => it.par_level > 0 && it.stock < it.par_level;

export const StockBar = ({ item }) => {
  const pct = item.par_level > 0 ? Math.min(100, (item.stock / (item.par_level * 2)) * 100) : 100;
  const color = item.stock <= 0 ? "bg-[#EF4444]" : isLow(item) ? "bg-[#F59E0B]" : "bg-[#10B981]";
  return (
    <div className="relative h-1.5 w-full bg-[#172033] rounded-sm overflow-hidden">
      <div className={`h-full ${color} transition-[width] duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
      {item.par_level > 0 && <div className="absolute top-0 left-1/2 h-full w-0.5 bg-white/40" title="Par level" />}
    </div>
  );
};

export function Stepper({ value, onChange, min = 0, step = 1, testId = "stepper", big = false }) {
  const size = big ? "h-16 w-16" : "h-12 w-12";
  const set = (v) => onChange(Math.max(min, Math.round(v * 1000) / 1000));
  const [txt, setTxt] = useState(String(value));
  useEffect(() => { setTxt((t) => (t === "" || t === "-" || t.endsWith(".") || Number(t) === value ? t : String(value))); }, [value]);
  const type = (raw) => {
    if (!/^-?\d*\.?\d*$/.test(raw) || (raw.startsWith("-") && min >= 0)) return;
    setTxt(raw);
    const n = Number(raw);
    onChange(raw === "" || raw === "-" || Number.isNaN(n) ? 0 : Math.max(min, n));
  };
  const step_ = (v) => { set(v); setTxt(String(Math.max(min, Math.round(v * 1000) / 1000))); };
  return (
    <div className="flex items-stretch gap-2" data-testid={testId}>
      <button type="button" data-testid={`${testId}-minus`} onClick={() => step_(value - step)}
        className={`${size} flex items-center justify-center bg-[#172033] border border-[#334155] rounded-md active:scale-95 transition-transform hover:border-[#F59E0B]`}>
        <Minus className={big ? "w-7 h-7" : "w-5 h-5"} />
      </button>
      <input type="text" inputMode={min < 0 ? "text" : "decimal"} data-testid={`${testId}-input`} value={txt} placeholder="0"
        onChange={(e) => type(e.target.value)} onBlur={() => { if (txt === "-" || txt.endsWith(".")) setTxt(String(value)); }}
        className={`${big ? "h-16 text-3xl w-32" : "h-12 text-xl w-24"} text-center font-mono font-bold bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]`} />
      <button type="button" data-testid={`${testId}-plus`} onClick={() => step_(value + step)}
        className={`${size} flex items-center justify-center bg-[#F59E0B] text-[#090D16] rounded-md active:scale-95 transition-transform hover:bg-[#FBBF24]`}>
        <Plus className={big ? "w-7 h-7" : "w-5 h-5"} />
      </button>
    </div>
  );
}

export const PageHeader = ({ kicker, title, children }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 rise">
    <div>
      <div className="font-mono text-xs tracking-[0.25em] text-[#F59E0B] mb-2">{kicker}</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Qty = ({ v, unit, className = "" }) => (
  <span className={`font-mono ${className}`}>{fmt(v)}<span className="text-[#6B7280] text-[0.7em] ml-1">{unit}</span></span>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`bg-[#0F1623] border border-[#1E293B] rounded-md ${className}`} {...rest}>{children}</div>
);
